import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { useFeatureIsOn } from '@growthbook/growthbook-react'

import styles from '../../styles/TopProgressBar.module.less'

// Simple top progress bar for route changes
// Shows on every route change start, progresses to 90% over time, completes on finish/error
const TopProgressBar = () => {
  const router = useRouter()
  // Enable progress bar based on feature flag
  const isEnabled = useFeatureIsOn('loading-progress-bar')
  const [isVisible, setIsVisible] = useState(false)
  const [progress, setProgress] = useState(0)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const startTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const clearTimers = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    if (startTimeoutRef.current) {
      clearTimeout(startTimeoutRef.current)
      startTimeoutRef.current = null
    }
  }

  const start = () => {
    clearTimers()
    // Slight delay prevents flicker for instant navigations
    startTimeoutRef.current = setTimeout(() => {
      setIsVisible(true)
      setProgress(10)

      // Ease towards 90% with diminishing increments
      timerRef.current = setInterval(() => {
        setProgress((p) => {
          if (p >= 90) return 90
          const delta = Math.max(0.5, (90 - p) * 0.05)
          return Math.min(90, p + delta)
        })
      }, 120)
    }, 120)
  }

  const done = () => {
    // Fast-complete
    clearTimers()
    setProgress(100)
    // Allow CSS transition to finish before hiding
    setTimeout(() => {
      setIsVisible(false)
      setProgress(0)
    }, 250)
  }

  useEffect(() => {
    if (!isEnabled) {
      return () => {}
    }
    const onStart = () => start()
    const onDone = () => done()

    router.events.on('routeChangeStart', onStart)
    router.events.on('routeChangeComplete', onDone)
    router.events.on('routeChangeError', onDone)

    return () => {
      clearTimers()
      router.events.off('routeChangeStart', onStart)
      router.events.off('routeChangeComplete', onDone)
      router.events.off('routeChangeError', onDone)
    }
  }, [router.events, isEnabled])

  if (!isEnabled || !isVisible) return null

  return (
    <div className={styles.wrapper}>
      <div
        className={styles.bar}
        style={{ width: `${progress}%` }}
      />
    </div>
  )
}

export default TopProgressBar
